// src/lib/statuses.ts
// -------------------------------------------------------------
// Single source of truth for statuses, open/closed sets, and chip colors
// -------------------------------------------------------------

import type { ChipProps } from "@mui/material/Chip";

/** Full catalog */
export const ALL_STATUSES = [
    "NEW",
    "QUALIFYING",
    "MATCHING",
    "TOUR_SCHEDULED",
    "PENDING_DECISION",
    "PLACED",
    "LOST",
    "CANCELLED",
    "CLOSED",
    "WITHDRAWN",
] as const;

export type LeadStatus = typeof ALL_STATUSES[number];

/** Open statuses (PLACED is not open) */
const BASE_OPEN: LeadStatus[] = [
    "NEW",
    "QUALIFYING",
    "MATCHING",
    "TOUR_SCHEDULED",
    "PENDING_DECISION",
];

export const OPEN_STATUSES_ARRAY: LeadStatus[] = [...BASE_OPEN];
export const OPEN_STATUSES: ReadonlySet<LeadStatus> = new Set(OPEN_STATUSES_ARRAY);

/** Closed statuses (everything not open) — derived to avoid drift */
export const CLOSED_STATUSES_ARRAY: LeadStatus[] = (
    ALL_STATUSES as readonly LeadStatus[]
).filter((s) => !OPEN_STATUSES.has(s));

export const CLOSED_STATUSES: ReadonlySet<LeadStatus> = new Set(CLOSED_STATUSES_ARRAY);

/** Chip color map (MUI). Single mapping to avoid switch duplication. */
export const STATUS_TO_CHIP_COLOR: Record<LeadStatus, ChipProps["color"]> = {
    NEW: "default",
    QUALIFYING: "info",
    MATCHING: "primary",
    TOUR_SCHEDULED: "warning",
    PENDING_DECISION: "info",
    PLACED: "success",
    LOST: "error",
    CANCELLED: "error",
    CLOSED: "default",
    WITHDRAWN: "default",
};

/** Helper: color by status with safe fallback */
export function statusToChipColor(status: string): ChipProps["color"] {
    return (STATUS_TO_CHIP_COLOR as Record<string, ChipProps["color"]>)[status] ?? "default";
}

/** Type guards + helpers */
export const isLeadStatus = (s: string): s is LeadStatus =>
    (ALL_STATUSES as readonly string[]).includes(s);

export const isOpenStatus = (s: string): boolean =>
    isLeadStatus(s) && OPEN_STATUSES.has(s);

export const isClosedStatus = (s: string): boolean =>
    isLeadStatus(s) && CLOSED_STATUSES.has(s);
